import { PLACEHOLDER_REGEX } from "@whilecat/core/editors/decorators/placeholder-decorator.js";
import type { CodeParamsView } from "@whilecat/core/tasks/common/params/code-task-params.ts";
import { VerificationError } from "@whilecat/core/tasks/common/verification-error.ts";
import type { ExecutionResult } from "./base-code-task.ts";

export abstract class CodeVerifier<T = string> {
    protected readonly codeParamsView: CodeParamsView<T>;

    constructor(codeParams: CodeParamsView<T>) {
        this.codeParamsView = codeParams;
    }

    /* Should throw VerificationError on failure. */
    abstract checkResult(result: ExecutionResult<T>): void;

    public getParamsView() {
        return this.codeParamsView;
    }

    public checkForErrors(result: ExecutionResult<T>) {
        if (result.result === "ERROR") {
            throw new VerificationError(result);
        }
    }

    /* Override to check that the entered code was correct. Should throw
    VerificationError on failure. */
    public checkAnswer(code: string) {
        return;
    }

    public predefinedResult(code: string): ExecutionResult<T> | undefined {
        const isExampleTask = this.codeParamsView.isExampleTask();
        const isCorrectOutputPredefined = this.codeParamsView.isCorrectOutputPredefined();

        const isCodeMatchesExpected = this.codeParamsView.isEditable() && this.codeParamsView.getCorrectCode() === code;

        if (isCorrectOutputPredefined && (isCodeMatchesExpected || isExampleTask)) {
            return this.codeParamsView.getPredefinedResult();
        }
        return undefined;
    }

    public checkPlaceholdersReplaced(code: string) {
        return !PLACEHOLDER_REGEX.test(code);
    }

    protected check(condition: boolean, result: ExecutionResult<T>, message: string) {
        if (!condition) {
            throw new VerificationError(result, message);
        }
    }
}
